// Geboortevenster · Verticale tijdlijn voor phone: tijd van boven naar beneden, lanes als kolommen, de tijdas links als scrubber met greep.
// Overzicht van de feature: EgituraPlatform/docs/human-design/hd-birth-window.md

import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  ElementRef,
  NgZone,
  computed,
  inject,
  input,
  output,
  signal,
  viewChild,
} from '@angular/core';
import { formatBirthClock } from '../birth-window-format';
import {
  HD_PROPERTY_LANE_SNAP_PX,
  HD_PROPERTY_LANE_VERTICAL_HEADER_HEIGHT_PX,
  HD_PROPERTY_LANE_VERTICAL_MAX_HEIGHT_PX,
  HD_PROPERTY_LANE_VERTICAL_MIN_HEIGHT_PX,
  HD_PROPERTY_LANE_VERTICAL_PX_PER_MINUTE,
  HD_PROPERTY_LANE_VERTICAL_RULER_WIDTH_PX,
  HdPropertyLaneBlockClick,
  HdPropertyLaneBlockVm,
  HdPropertyLaneGroupVm,
  HdPropertyLaneLabelPart,
  HdPropertyLaneVm,
} from './hd-property-lanes.models';
import { birthClockMinuteOfDay, fitLaneLabelParts, laneAccentColors } from './hd-property-lanes.text';

interface RenderBlock {
  readonly xPx: number;
  readonly yPx: number;
  readonly widthPx: number;
  readonly heightPx: number;
  /** Label gedraaid langs de tijdas (van onder naar boven), enkel in blokken die hoog en breed genoeg zijn. */
  readonly parts: readonly HdPropertyLaneLabelPart[];
  readonly block: HdPropertyLaneBlockVm;
  readonly fill?: string;
  readonly stroke?: string;
  readonly textFill?: string;
}

interface RenderMarker {
  readonly yPx: number;
  readonly glyph: string;
  readonly accent: string;
  readonly title: string;
  readonly text: string;
}

interface RenderColumn {
  readonly key: string;
  readonly laneKey: string;
  readonly label: string;
  readonly subLabel?: string;
  readonly xPx: number;
  readonly widthPx: number;
  readonly blocks: readonly RenderBlock[];
  readonly markers: readonly RenderMarker[];
}

interface RenderGroup {
  readonly key: string;
  readonly label: string;
  readonly hint?: string;
  readonly color?: string;
  readonly muted: boolean;
  readonly columns: readonly RenderColumn[];
}

interface RulerTick {
  readonly yPx: number;
  readonly label: string;
  readonly major: boolean;
}

const MINUTE_MS = 60_000;
const TICK_STEPS_MIN = [1, 2, 5, 10, 15, 30, 60, 120, 180, 360];
/** Verticaal is er per tick één regel tekst nodig; dit is de minimale afstand tussen twee labels. */
const MIN_TICK_SPACING_PX = 28;
const DRAG_THRESHOLD_PX = 4;
const COLUMN_GAP_PX = 3;
/** Onder deze maten krijgt een blok geen tekst (de waardestrook van het paneel leest dan voor). */
const MIN_LABEL_HEIGHT_PX = 34;
const MIN_LABEL_WIDTH_PX = 16;
/** Een markerkolom (wissellog) is zoveel keer zo breed als een gewone kolom. */
const MARKER_COLUMN_WEIGHT = 3;

/**
 * Verticale variant van `hd-property-lanes` voor de phone-sheet (Steven
 * 2026-10-17): de tijd loopt van boven naar beneden zodat gewoon scrollen door
 * de tijd gaat, de lanes van één groep staan als kolommen naast een tijdas.
 * De tijdas links is de scrubber: slepen (met greep) of tikken zet de
 * proeftijd; de kolommen scrollen native en reageren enkel op tikken.
 * Data en uitgangen zijn identiek aan de horizontale component.
 */
@Component({
  selector: 'hd-property-lanes-vertical',
  templateUrl: './hd-property-lanes-vertical.component.html',
  styleUrl: './hd-property-lanes-vertical.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class HdPropertyLanesVerticalComponent implements AfterViewInit {
  readonly groups = input.required<readonly HdPropertyLaneGroupVm[]>();
  readonly fromMs = input.required<number>();
  readonly toMs = input.required<number>();
  readonly anchorMs = input.required<number>();
  readonly anchorLabel = input('');
  readonly markerMs = input<number | undefined>(undefined);
  readonly markerLabel = input('');
  readonly markerDraggable = input(false);
  readonly markerHandleLabel = input('');
  readonly timeZone = input<string | undefined>(undefined);
  readonly lang = input('nl');
  readonly ariaLabel = input('');

  readonly blockClicked = output<HdPropertyLaneBlockClick>();
  readonly markerChanged = output<number>();
  readonly markerCommitted = output<number>();
  readonly emptyClicked = output<number>();
  readonly groupToggled = output<string>();

  protected readonly rulerWidth = HD_PROPERTY_LANE_VERTICAL_RULER_WIDTH_PX;
  protected readonly headerHeight = HD_PROPERTY_LANE_VERTICAL_HEADER_HEIGHT_PX;

  private readonly host = viewChild.required<ElementRef<HTMLElement>>('host');
  private readonly zone = inject(NgZone);
  private readonly destroyRef = inject(DestroyRef);

  private readonly hostWidthPx = signal(0);
  private dragPointerId: number | undefined;
  private dragStartY = 0;
  private dragging = false;
  private dragTarget: Element | undefined;
  private pendingFrame: number | undefined;
  private pendingMs: number | undefined;
  private suppressNextClick = false;

  protected readonly spanMs = computed(() => Math.max(1, this.toMs() - this.fromMs()));

  protected readonly heightPx = computed(() => {
    const natural = (this.spanMs() / MINUTE_MS) * HD_PROPERTY_LANE_VERTICAL_PX_PER_MINUTE;
    return Math.round(Math.min(HD_PROPERTY_LANE_VERTICAL_MAX_HEIGHT_PX, Math.max(HD_PROPERTY_LANE_VERTICAL_MIN_HEIGHT_PX, natural)));
  });

  protected readonly pxPerMs = computed(() => this.heightPx() / this.spanMs());
  protected readonly columnsWidthPx = computed(() => Math.max(120, this.hostWidthPx() - this.rulerWidth));

  protected readonly anchorYPx = computed(() => this.toPx(this.anchorMs()));
  protected readonly markerYPx = computed(() => {
    const ms = this.markerMs();
    return ms === undefined ? undefined : this.toPx(ms);
  });

  protected readonly renderGroups = computed<RenderGroup[]>(() => {
    const totalWidth = this.columnsWidthPx();
    return this.groups().map(group => {
      const lanes = group.collapsed === true ? group.collapsedLanes ?? group.lanes : group.lanes;
      const weights = lanes.map(lane => columnWeight(lane));
      const totalWeight = Math.max(1, weights.reduce((sum, weight) => sum + weight, 0));
      const unit = totalWidth / totalWeight;
      let x = 0;
      const columns: RenderColumn[] = [];
      lanes.forEach((lane, index) => {
        const width = unit * weights[index];
        columns.push(...this.renderLane(lane, x, width));
        x += width;
      });
      return { key: group.key, label: group.label, hint: group.hint, color: group.color, muted: group.muted === true, columns };
    });
  });

  private renderLane(lane: HdPropertyLaneVm, xPx: number, widthPx: number): RenderColumn[] {
    if (lane.tracks && lane.tracks.length > 0) {
      const trackWidth = widthPx / lane.tracks.length;
      return lane.tracks.map((track, index) => ({
        key: `${lane.key}:${track.key}`,
        laneKey: lane.key,
        label: index === 0 ? lane.label : '',
        subLabel: track.key.split(':').pop()?.slice(0, 1).toUpperCase(),
        xPx: xPx + index * trackWidth,
        widthPx: trackWidth,
        blocks: track.blocks
          .map(block => this.renderBlock({ ...block, accent: block.accent ?? track.accent }, xPx + index * trackWidth, trackWidth))
          .filter((block): block is RenderBlock => block != null),
        markers: [],
      }));
    }
    if (lane.markers && lane.markers.length > 0) {
      const from = this.fromMs();
      const to = this.toMs();
      const markers = lane.markers
        .filter(marker => marker.ms >= from && marker.ms <= to)
        .map<RenderMarker>(marker => ({
          yPx: this.toPx(marker.ms),
          glyph: marker.glyph,
          accent: marker.accent,
          title: marker.title,
          text: fitLaneLabelParts([{ text: marker.shortLabel ?? marker.title, tone: 'neutral' }], widthPx - 22).map(part => part.text).join(''),
        }));
      return [{ key: lane.key, laneKey: lane.key, label: lane.label, xPx, widthPx, blocks: [], markers }];
    }
    const blocks = lane.blocks
      .map(block => this.renderBlock(block, xPx, widthPx))
      .filter((block): block is RenderBlock => block != null);
    return [{ key: lane.key, laneKey: lane.key, label: lane.label, xPx, widthPx, blocks, markers: [] }];
  }

  private renderBlock(block: HdPropertyLaneBlockVm, columnX: number, columnWidth: number): RenderBlock | undefined {
    const from = this.fromMs();
    const to = this.toMs();
    if (block.toMs <= from || block.fromMs >= to) return undefined;
    const start = Math.max(block.fromMs, from);
    const end = Math.min(block.toMs, to);
    const yPx = this.toPx(start);
    const heightPx = Math.max(2, (end - start) * this.pxPerMs());
    const widthPx = Math.max(2, columnWidth - COLUMN_GAP_PX);
    const colors = block.accent ? laneAccentColors(block.accent, block.state) : {};
    const parts = heightPx >= MIN_LABEL_HEIGHT_PX && widthPx >= MIN_LABEL_WIDTH_PX
      ? fitLaneLabelParts(block.parts ?? [{ text: block.label, tone: 'neutral' }], heightPx)
      : [];
    return { xPx: columnX + COLUMN_GAP_PX / 2, yPx, widthPx, heightPx, parts, block, ...colors };
  }

  protected readonly ticks = computed<RulerTick[]>(() => {
    const pxPerMs = this.pxPerMs();
    const from = this.fromMs();
    const to = this.toMs();
    const zone = this.timeZone();
    const lang = this.lang();
    const stepMin = TICK_STEPS_MIN.find(step => step * MINUTE_MS * pxPerMs >= MIN_TICK_SPACING_PX) ?? TICK_STEPS_MIN[TICK_STEPS_MIN.length - 1];
    const stepMs = stepMin * MINUTE_MS;
    const minuteOfDay = birthClockMinuteOfDay(from, zone);
    const remainder = ((minuteOfDay % stepMin) + stepMin) % stepMin;
    let tickMs = from + (remainder === 0 ? 0 : (stepMin - remainder) * MINUTE_MS);
    const ticks: RulerTick[] = [];
    while (tickMs <= to) {
      const minute = birthClockMinuteOfDay(tickMs, zone);
      ticks.push({ yPx: (tickMs - from) * pxPerMs, label: formatBirthClock(tickMs, zone, lang), major: minute % 60 === 0 });
      tickMs += stepMs;
    }
    return ticks;
  });

  private readonly snapTargets = computed<number[]>(() => {
    const set = new Set<number>([this.anchorMs()]);
    const addBlocks = (blocks: readonly HdPropertyLaneBlockVm[]) => {
      for (const block of blocks) {
        set.add(block.fromMs);
        set.add(block.toMs);
      }
    };
    for (const group of this.groups()) {
      for (const lane of [...group.lanes, ...(group.collapsedLanes ?? [])]) {
        addBlocks(lane.blocks);
        for (const track of lane.tracks ?? []) addBlocks(track.blocks);
        for (const marker of lane.markers ?? []) set.add(marker.ms);
      }
    }
    return [...set];
  });

  ngAfterViewInit(): void {
    const host = this.host().nativeElement;
    const measure = () => {
      const width = host.clientWidth;
      this.zone.run(() => this.hostWidthPx.set(width));
    };
    measure();
    if (typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(() => measure());
    observer.observe(host);
    this.destroyRef.onDestroy(() => observer.disconnect());
  }

  protected toPx(ms: number): number {
    return (ms - this.fromMs()) * this.pxPerMs();
  }

  protected signOf(part: HdPropertyLaneLabelPart): string {
    return part.tone === 'added' || part.tone === 'removed' ? part.text.slice(0, 1) : '';
  }

  protected restOf(part: HdPropertyLaneLabelPart): string {
    return part.tone === 'added' || part.tone === 'removed' ? part.text.slice(1) : part.text;
  }

  protected onBlockClick(groupKey: string, laneKey: string, block: HdPropertyLaneBlockVm, event: MouseEvent): void {
    if (this.suppressNextClick) {
      this.suppressNextClick = false;
      return;
    }
    event.stopPropagation();
    this.blockClicked.emit({ groupKey, laneKey, block, origin: { x: event.clientX, y: event.clientY } });
  }

  protected onBlockKeydown(groupKey: string, laneKey: string, block: HdPropertyLaneBlockVm, event: KeyboardEvent): void {
    if (event.key !== 'Enter' && event.key !== ' ') return;
    event.preventDefault();
    const rect = (event.currentTarget as Element).getBoundingClientRect();
    this.blockClicked.emit({ groupKey, laneKey, block, origin: { x: rect.left + rect.width / 2, y: rect.top + rect.height } });
  }

  /** Tik op een kolom buiten een blok: proeftijd op die hoogte (de kolommen scrollen native, geen slepen). */
  protected onColumnsClick(event: MouseEvent): void {
    const onBlock = (event.target as Element | null)?.closest?.('.plv-block') != null;
    if (onBlock) return;
    this.emptyClicked.emit(this.snap(this.msFromClientY(event.clientY, event.currentTarget as Element)));
  }

  // De tijdas: pointerdown + verticaal slepen = proeftijd verslepen (met capture), tik = proeftijd zetten.
  protected onRulerPointerDown(event: PointerEvent): void {
    if (event.button !== 0) return;
    this.dragPointerId = event.pointerId;
    this.dragStartY = event.clientY;
    this.dragging = false;
    this.dragTarget = event.currentTarget as Element;
    this.suppressNextClick = false;
  }

  protected onRulerPointerMove(event: PointerEvent): void {
    if (this.dragPointerId !== event.pointerId || !this.markerDraggable()) return;
    if (!this.dragging) {
      if (Math.abs(event.clientY - this.dragStartY) < DRAG_THRESHOLD_PX) return;
      this.dragging = true;
      this.suppressNextClick = true;
      this.dragTarget?.setPointerCapture?.(event.pointerId);
    }
    this.queueMarker(this.msFromClientY(event.clientY, this.dragTarget));
  }

  protected onRulerPointerUp(event: PointerEvent): void {
    if (this.dragPointerId !== event.pointerId) return;
    const target = this.dragTarget;
    this.dragPointerId = undefined;
    this.dragTarget = undefined;
    if (this.dragging) {
      this.dragging = false;
      this.flushMarker();
      this.markerCommitted.emit(this.snap(this.msFromClientY(event.clientY, target)));
      target?.releasePointerCapture?.(event.pointerId);
      return;
    }
    if (this.markerDraggable()) this.emptyClicked.emit(this.snap(this.msFromClientY(event.clientY, target)));
  }

  protected onRulerPointerCancel(event: PointerEvent): void {
    if (this.dragPointerId !== event.pointerId) return;
    this.dragPointerId = undefined;
    this.dragging = false;
    this.dragTarget = undefined;
  }

  private msFromClientY(clientY: number, target: Element | undefined): number {
    if (!target) return this.anchorMs();
    const rect = target.getBoundingClientRect();
    const y = Math.min(Math.max(0, clientY - rect.top), this.heightPx());
    return this.fromMs() + y / this.pxPerMs();
  }

  private snap(ms: number): number {
    const pxPerMs = this.pxPerMs();
    let best = Math.round(ms / 1000) * 1000;
    let bestDistance = HD_PROPERTY_LANE_SNAP_PX;
    for (const target of this.snapTargets()) {
      const distance = Math.abs(target - ms) * pxPerMs;
      if (distance < bestDistance) {
        bestDistance = distance;
        best = target;
      }
    }
    return Math.min(Math.max(best, this.fromMs()), this.toMs());
  }

  private queueMarker(ms: number): void {
    this.pendingMs = this.snap(ms);
    if (this.pendingFrame !== undefined) return;
    this.pendingFrame = requestAnimationFrame(() => {
      this.pendingFrame = undefined;
      this.flushMarker();
    });
  }

  private flushMarker(): void {
    if (this.pendingFrame !== undefined) {
      cancelAnimationFrame(this.pendingFrame);
      this.pendingFrame = undefined;
    }
    if (this.pendingMs === undefined) return;
    const ms = this.pendingMs;
    this.pendingMs = undefined;
    this.zone.run(() => this.markerChanged.emit(ms));
  }
}

function columnWeight(lane: HdPropertyLaneVm): number {
  if (lane.tracks && lane.tracks.length > 0) return lane.tracks.length;
  if (lane.markers && lane.markers.length > 0) return MARKER_COLUMN_WEIGHT;
  return 1;
}
